// ════════════════════════════════════════════════════════════════════════
//  PASO 2 DE RECUPERAR LA CONTRASEÑA — "escribe la nueva"
//
//  Aquí se llega desde el enlace del correo, con el token en la dirección
//  (?token=...). Ese token es, durante 30 minutos, la llave de la cuenta.
//
//  Lo primero que hace la pantalla es PREGUNTAR si el enlace sigue valiendo,
//  antes de enseñar ningún campo. Sin eso, quien abre un enlace de ayer escribe
//  su contraseña nueva dos veces, pulsa el botón y solo entonces se entera de
//  que no servía — y encima sin saber qué hacer a continuación.
// ════════════════════════════════════════════════════════════════════════
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import API, { mensajeDeError } from '../api';
import './Login.css';
import { useT } from '../i18n';
import LangToggle from '../components/LangToggle';
import { IS_PHONE } from '../device';
import { useLogo } from '../theme';

const MIN_PASSWORD = 8;

export default function ResetPassword() {
  const logoSrc = useLogo();
  const [params] = useSearchParams();
  const token = params.get('token') || '';

  // 'comprobando' | 'valido' | 'invalido'
  const [estado, setEstado] = useState('comprobando');
  const [password, setPassword] = useState('');
  const [repetida, setRepetida] = useState('');
  const [error, setError] = useState('');
  const [enviando, setEnviando] = useState(false);
  const navigate = useNavigate();
  const t = useT();

  useEffect(() => {
    let vigente = true;
    if (!token) {
      setEstado('invalido');
      return undefined;
    }
    axios
      .get(`${API}/auth/reset-password/check`, { params: { token } })
      .then((res) => {
        if (vigente) setEstado(res.data.valido ? 'valido' : 'invalido');
      })
      // Si la comprobación no llega a contestar (sin red, servidor caído), se
      // deja pasar al formulario en vez de dar el enlace por malo: el servidor
      // volverá a comprobarlo al enviar, que es donde la decisión cuenta de
      // verdad. Decirle "tu enlace no vale" a alguien cuyo enlace sí valía lo
      // manda a pedir otro sin necesidad.
      .catch(() => {
        if (vigente) setEstado('valido');
      });
    // Evita avisar a un componente que ya no está en pantalla si el usuario se
    // va antes de que conteste el servidor.
    return () => { vigente = false; };
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (enviando) return;
    setError('');

    if (password.length < MIN_PASSWORD) {
      setError(t('auth.password.tooShort', { min: MIN_PASSWORD }));
      return;
    }
    // Se piden dos veces porque el campo va oculto: una errata al teclear no se
    // ve, y sin esta comprobación el usuario se quedaría fuera de su cuenta con
    // una contraseña que nunca quiso poner y que no puede leer en ningún sitio.
    if (password !== repetida) {
      setError(t('auth.reset.mismatch'));
      return;
    }

    setEnviando(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, password });
      navigate('/', { state: { avisoKey: 'auth.reset.done' } });
    } catch (err) {
      setError(mensajeDeError(err, t('auth.reset.errorFallback')));
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="login-page">
      <LangToggle style={{ position: 'fixed', top: 12, right: 16, zIndex: 10 }} />
      {/* Teléfono: sin vídeo de fondo (pesa y gasta batería); queda el degradado. */}
      {!IS_PHONE && (
        <video className="login-video-bg" autoPlay muted playsInline loop>
          <source src="/videos/background.mp4" type="video/mp4" />
        </video>
      )}

      <div className="login-overlay" />

      <div className="login-container">
        <div className="login-card-glow">
          <div className="login-card">
            <div className="login-logo">
              <img src={logoSrc} alt="DragonAudio" className="logo-img" />
              <h1 className="login-title">DragonAudio</h1>
            </div>

            {estado === 'comprobando' && (
              <p className="login-pista login-pista--centrada">{t('auth.reset.checking')}</p>
            )}

            {estado === 'invalido' && (
              <>
                <p className="login-error">
                  {t('auth.reset.invalid')}
                </p>
                <p className="login-pista login-pista--centrada">
                  {t('auth.reset.invalidHint')}
                </p>
                <p className="login-register">
                  <Link to="/forgot-password">{t('auth.reset.requestNew')}</Link>
                </p>
              </>
            )}

            {estado === 'valido' && (
              <>
                <h2 className="login-subtitulo">{t('auth.reset.title')}</h2>

                {error && <p className="login-error">{error}</p>}

                <form onSubmit={handleSubmit} className="login-form">
                  <input
                    type="password" placeholder={t('auth.reset.newPassword')} value={password}
                    onChange={e => setPassword(e.target.value)}
                    className="login-input"
                    autoComplete="new-password" required autoFocus
                  />
                  <input
                    type="password" placeholder={t('auth.reset.repeat')} value={repetida}
                    onChange={e => setRepetida(e.target.value)}
                    className="login-input"
                    autoComplete="new-password" required
                  />
                  <p className="login-pista">
                    {t('auth.password.hint', { min: MIN_PASSWORD })}
                  </p>
                  <button type="submit" className="login-btn" disabled={enviando}>
                    {enviando ? t('auth.reset.saving') : t('auth.reset.submit')}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
